import { clsx, type ClassValue } from 'clsx'
import { extendTailwindMerge } from 'tailwind-merge'

/*
 * tailwind-merge ships a default class-group config that only knows
 * Tailwind's own scales. It has no way to see the custom font-size scale this
 * project defines in theme.css (`--text-h1`…`--text-h6`, `--text-base`,
 * `--text-small`, `--text-large`), so `text-small`, `text-h1`, etc. fell
 * through to its generic "unrecognized text-* utility" bucket — the SAME
 * bucket as `text-{color}` utilities like `text-white` or
 * `text-font-secondary`.
 *
 * Two classes landing in one bucket means tailwind-merge treats them as
 * conflicting and keeps only the last one written, silently dropping whichever
 * came first — the size or the color, depending on argument order. That is
 * exactly what happened to the cookie banner's Decline button: `outlineWhite`
 * sets `text-white`, the `small` size variant adds `text-small` after it, and
 * the color silently disappeared. `CaseStudyGrid`'s author title had the same
 * bug the other way round — `text-small` came first, so IT was the one
 * dropped, and the label rendered at default paragraph size.
 *
 * Registering the scale as its own `font-size` group fixes both directions,
 * everywhere on the site, not just the one button.
 *
 * Tailwind's stock config also treats the `font-size` group as conflicting
 * with `leading` and `font-weight` — real utilities like `text-lg` bundle a
 * default line-height, so an explicit `leading-*` is meant to replace it. Our
 * theme already handles that override at the CSS level: `h1`–`h6` pair
 * `--text-h1--line-height` etc. through `var(--tw-leading, …)`, which lets an
 * explicit `leading-*` win at render time with no merge-time help needed, and
 * `base`/`small`/`large` carry no paired line-height at all. Left as the
 * default, that conflict rule made tailwind-merge delete `leading-[1em]` from
 * `Button`'s own base classes the moment any of our sizes joined it — cleared
 * here since nothing in this codebase depends on the stock behaviour.
 */
/*
 * The same blind spot as the font-size scale above, on two more axes — and this
 * one was load-bearing.
 *
 * tailwind-merge only knows conflicts between classes it can parse. It has no
 * idea `section-lg` is a spacing value or `blog-post` a container width, so
 * `py-section-lg` never landed in the `py` group and `max-w-blog-post` never
 * landed in `max-w`. Conflicting utilities therefore BOTH survived the merge
 * and the CSS emit order silently picked the winner — which is not the order
 * they were written in.
 *
 * Two consequences, both of which were live on the site:
 *
 *   - `SectionWrapper`'s base carried `max-w-main` (1240px) and every `width`
 *     variant added a second `max-w-*`. `blogPost` (740px) and `content`
 *     (760px) lost, so blog posts rendered on a 1240px measure.
 *   - `<Section padding="lg" className="pb-16">` kept all of `py-section-lg`
 *     and `pb-16`, and the className lost. Authors reached for `!pb-16` to win,
 *     which is where most of the codebase's `!important` came from.
 *
 * Registering the scales fixes both, and makes a className override mean what
 * it says. Any new `--spacing-section-*` or `--container-*` token has to be
 * added here too.
 */
const SECTION_SPACING = ['xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl']
const CONTAINERS = ['main', 'xsmall', 'small', 'blog-post', 'content', 'medium']

const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [
        'text-h1',
        'text-h2',
        'text-h3',
        'text-h4',
        'text-h5',
        'text-h6',
        'text-base',
        'text-small',
        'text-large',
      ],
      'max-w': CONTAINERS.map((c) => `max-w-${c}`),
      p: SECTION_SPACING.map((s) => `p-section-${s}`),
      px: SECTION_SPACING.map((s) => `px-section-${s}`),
      py: SECTION_SPACING.map((s) => `py-section-${s}`),
      pt: SECTION_SPACING.map((s) => `pt-section-${s}`),
      pr: SECTION_SPACING.map((s) => `pr-section-${s}`),
      pb: SECTION_SPACING.map((s) => `pb-section-${s}`),
      pl: SECTION_SPACING.map((s) => `pl-section-${s}`),
      gap: SECTION_SPACING.map((s) => `gap-section-${s}`),
    },
  },
  override: {
    conflictingClassGroups: {
      'font-size': [],
    },
  },
})

/** Merge conditional class names, with later Tailwind utilities winning. */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}
